import React, { useState, useCallback } from 'react'
import { Button, Input, Space, Card, Tag, Tooltip, Typography } from 'antd'
import TextArea from 'antd/es/input/TextArea'
import styled from 'styled-components'
import {
  IconUsers,
  IconBuildingBank,
  IconPhone,
  IconMessageCircle,
  IconGripVertical,
  IconEdit,
  IconCheck,
  IconX
} from '@tabler/icons-react'
import DraggableTable from '../DraggableTable'
import DeleteGroup from '../DeleteGroup'
import SaveGroup from '../SaveGroup'
import SendMessage from '../SendMessage'
import ParentPartner from '../ParentPartner'
import GSlipageList from '../GSlipageList'
import API from '../../../utils/api'
import { useToken } from 'app/hooks'

const { Text } = Typography

const StyledCard = styled(Card)`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;

  .ant-card-head {
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    background: rgba(255, 255, 255, 0.03);
  }

  .ant-card-head-title {
    color: #ffffff;
    font-weight: 600;
  }
`

const GroupCodeBadge = styled(Tag)`
  background: linear-gradient(45deg, #667eea, #764ba2);
  border: none;
  color: white;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 8px;
  font-size: 12px;
`

const UserCountButton = styled(Button)`
  background: linear-gradient(45deg, #4facfe, #00f2fe);
  border: none;
  border-radius: 8px;
  color: white;
  font-weight: 600;

  &:hover {
    background: linear-gradient(45deg, #00f2fe, #4facfe);
    color: white;
  }
`

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 200px;
`

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
`

const BankInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  min-width: 180px;
`

const ContactInfo = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`

const ContactButton = styled(Button)`
  font-size: 11px;
  height: 24px;
  padding: 0 8px;
  border-radius: 4px;

  &.cs1 {
    background: #52c41a;
    border-color: #52c41a;
    color: white;
  }

  &.cs2 {
    background: #1890ff;
    border-color: #1890ff;
    color: white;
  }

  &.cs3 {
    background: #722ed1;
    border-color: #722ed1;
    color: white;
  }

  &.cs4 {
    background: #fa8c16;
    border-color: #fa8c16;
    color: white;
  }
`

const OrderInput = styled(Input)`
  width: 60px;
  text-align: center;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #ffffff;

  &:focus {
    background: rgba(255, 255, 255, 0.15);
    border-color: #1890ff;
  }
`

interface EnhancedUserGroupTableProps {
  dataSource: any[]
  onDataChange: () => void
  loading?: boolean
}

const EnhancedUserGroupTable: React.FC<EnhancedUserGroupTableProps> = ({
  dataSource,
  onDataChange,
  loading = false
}) => {
  const { data: token } = useToken()
  const [editingSeq, setEditingSeq] = useState<{ [key: string]: boolean }>({})

  // Ensure dataSource is always an array
  const safeDataSource = Array.isArray(dataSource) ? dataSource : []

  const handleDragEnd = useCallback(async (dragIndex: number, hoverIndex: number) => {
    try {
      const newData = [...safeDataSource]
      const draggedItem = newData[dragIndex]
      const hoveredItem = newData[hoverIndex]

      // Swap the seq values
      const tempSeq = draggedItem.seq
      draggedItem.seq = hoveredItem.seq
      hoveredItem.seq = tempSeq

      // Update both items on the server
      if (token) {
        await Promise.all([
          API.put(`/admin/usergroup/${draggedItem.id}`, token, { seq: draggedItem.seq }),
          API.put(`/admin/usergroup/${hoveredItem.id}`, token, { seq: hoveredItem.seq })
        ])
      }

      onDataChange()
    } catch (error) {
      console.error('Failed to update order:', error)
    }
  }, [safeDataSource, token, onDataChange])

  const columns = [
    {
      title: '코드',
      dataIndex: 'id',
      align: 'center' as const,
      width: 80,
      render: (text: string) => (
        <GroupCodeBadge>#{text}</GroupCodeBadge>
      ),
    },
    {
      title: '순서',
      dataIndex: 'seq',
      align: 'center' as const,
      width: 80,
      render: (text: string, record: any) => (
        <OrderInput
          value={record.seq}
          onChange={(e) => {
            record.seq = e.target.value
          }}
          onBlur={() => {
            // Auto-save when user finishes editing
            if (token) {
              API.put(`/admin/usergroup/${record.id}`, token, { seq: record.seq })
                .then(() => onDataChange())
                .catch(console.error)
            }
          }}
        />
      ),
    },
    {
      title: '그룹정보',
      dataIndex: 'name',
      width: 250,
      render: (text: string, record: any) => (
        <InfoSection>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <Input
              style={{
                fontSize: 14,
                fontWeight: 600,
                background: 'rgba(255,255,255,0.1)',
                border: '1px solid rgba(255,255,255,0.2)',
                color: '#ffffff'
              }}
              defaultValue={record.name}
              onChange={(e) => { record.name = e.target.value }}
            />
          </div>
          <UserCountButton
            size="small"
            icon={<IconUsers size={14} />}
            onClick={() => {
              window.location.href = '/users?groupid=' + record.id
            }}
          >
            회원 {record.usercount}명
          </UserCountButton>
        </InfoSection>
      ),
    },
    {
      title: '입출금 정보',
      dataIndex: 'info',
      width: 300,
      render: (text: string, record: any) => {
        if (record?.subgroup) return null
        return (
          <InfoSection>
            <div style={{ marginBottom: 8 }}>
              <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, marginBottom: 4, display: 'block' }}>
                입금안내
              </Text>
              <TextArea
                size="small"
                rows={2}
                style={{
                  fontSize: 11,
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  color: '#ffffff'
                }}
                defaultValue={record.info_d}
                onChange={(e) => { record.info_d = e.target.value }}
              />
            </div>
            <div>
              <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, marginBottom: 4, display: 'block' }}>
                출금안내
              </Text>
              <TextArea
                size="small"
                rows={2}
                style={{
                  fontSize: 11,
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  color: '#ffffff'
                }}
                defaultValue={record.info_w}
                onChange={(e) => { record.info_w = e.target.value }}
              />
            </div>
          </InfoSection>
        )
      },
    },
    {
      title: '계좌정보',
      dataIndex: 'bank',
      align: 'center' as const,
      width: 220,
      render: (text: string, record: any) => {
        if (record?.subgroup) return null
        return (
          <BankInfo>
            <InfoItem>
              <IconBuildingBank size={14} />
              <Input
                placeholder="은행명"
                size="small"
                style={{
                  width: '100%',
                  fontSize: 11,
                  background: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  color: '#ffffff'
                }}
                defaultValue={record.kor_1}
                onChange={(e) => { record.kor_1 = e.target.value }}
              />
            </InfoItem>
            <InfoItem>
              <Input
                placeholder="계좌번호"
                size="small"
                style={{
                  fontSize: 11,
                  background: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  color: '#ffffff'
                }}
                defaultValue={record.kor_2}
                onChange={(e) => { record.kor_2 = e.target.value }}
              />
            </InfoItem>
            <InfoItem>
              <Input
                placeholder="예금주"
                size="small"
                style={{
                  fontSize: 11,
                  background: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  color: '#ffffff'
                }}
                defaultValue={record.kor_3}
                onChange={(e) => { record.kor_3 = e.target.value }}
              />
            </InfoItem>
          </BankInfo>
        )
      },
    },
    {
      title: '고객센터',
      dataIndex: 'contact',
      align: 'center' as const,
      width: 180,
      render: (text: string, record: any) => {
        if (record?.subgroup) return null
        return (
          <InfoSection>
            <div style={{ marginBottom: 8 }}>
              <ContactInfo>
                {record.cs_1 && (
                  <Tooltip title="고객센터1">
                    <ContactButton className="cs1" size="small">
                      <IconPhone size={12} />
                    </ContactButton>
                  </Tooltip>
                )}
                {record.cs_2 && (
                  <Tooltip title="고객센터2">
                    <ContactButton className="cs2" size="small">
                      <IconPhone size={12} />
                    </ContactButton>
                  </Tooltip>
                )}
                {record.cs_3 && (
                  <Tooltip title="텔레상담">
                    <ContactButton className="cs3" size="small">
                      <IconMessageCircle size={12} />
                    </ContactButton>
                  </Tooltip>
                )}
                {record.cs_4 && (
                  <Tooltip title="카톡상담">
                    <ContactButton className="cs4" size="small">
                      <IconMessageCircle size={12} />
                    </ContactButton>
                  </Tooltip>
                )}
              </ContactInfo>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <Input
                placeholder="고객센터1"
                size="small"
                style={{
                  fontSize: 10,
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  color: '#ffffff'
                }}
                defaultValue={record.cs_1}
                onChange={(e) => { record.cs_1 = e.target.value }}
              />
              <Input
                placeholder="고객센터2"
                size="small"
                style={{
                  fontSize: 10,
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  color: '#ffffff'
                }}
                defaultValue={record.cs_2}
                onChange={(e) => { record.cs_2 = e.target.value }}
              />
              <Input
                placeholder="텔레상담링크"
                size="small"
                style={{
                  fontSize: 10,
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  color: '#ffffff'
                }}
                defaultValue={record.cs_3}
                onChange={(e) => { record.cs_3 = e.target.value }}
              />
              <Input
                placeholder="상담링크"
                size="small"
                style={{
                  fontSize: 10,
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  color: '#ffffff'
                }}
                defaultValue={record.cs_4}
                onChange={(e) => { record.cs_4 = e.target.value }}
              />
            </div>
          </InfoSection>
        )
      },
    },
    {
      title: '상위파트너',
      dataIndex: 'parentid',
      align: 'center' as const,
      width: 120,
      render: (text: string, record: any) => (
        <ParentPartner data={record} />
      ),
    },
    {
      title: '슬리피지/노출',
      dataIndex: 'slippage',
      align: 'center' as const,
      width: 120,
      render: (text: string, record: any) => (
        <GSlipageList group_id={record.id} />
      ),
    },
    {
      title: '관리',
      dataIndex: 'manage',
      align: 'center' as const,
      width: 120,
      render: (text: string, record: any) => (
        <Space.Compact size="small">
          <SaveGroup data={record} mutate={onDataChange} />
          <DeleteGroup data={record} mutate={onDataChange} />
          <SendMessage target="group" targetid={record.id} />
        </Space.Compact>
      ),
    },
  ]

  return (
    <StyledCard>
      <DraggableTable
        columns={columns}
        dataSource={safeDataSource}
        onDragEnd={handleDragEnd}
        rowKey="id"
        loading={loading}
        scroll={{ x: 1400 }}
        pagination={{
          pageSize: 50,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} / ${total}개`,
        }}
      />
    </StyledCard>
  )
}

export default EnhancedUserGroupTable